import React, { useEffect, useState, createElement } from 'react';
import { History, Download, FileText, Building2, Users, UtensilsCrossed, Settings, Clock } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { Pagination } from '../components/ui/Pagination';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { getSupabase } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { exportActivityLogToExcel } from '../utils/excelExport';
export function ActivityLogPage() {
  useDocumentTitle('Registro');
  const {
    logs
  } = useActivityLog();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('Todos');
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Sincronización en tiempo real
  useEffect(() => {
    const client = getSupabase();
    if (!client) return;
    const subscription = client.channel('activity-logs-channel').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'activity_logs'
    }, () => {
      // El contexto ActivityLogContext ya maneja la recarga
    }).subscribe();
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.action.toLowerCase().includes(searchTerm.toLowerCase()) || log.details.toLowerCase().includes(searchTerm.toLowerCase()) || log.user.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'Todos' || log.type === typeFilter.toLowerCase();
    return matchesSearch && matchesType;
  });
  // Paginación
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset a primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter]);
  const getIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <FileText size={16} data-id="element-1206" />;
      case 'company':
        return <Building2 size={16} data-id="element-1207" />;
      case 'employee':
        return <Users size={16} data-id="element-1208" />;
      case 'menu':
        return <UtensilsCrossed size={16} data-id="element-1209" />;
      default:
        return <Settings size={16} data-id="element-1210" />;
    }
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'order':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
      case 'company':
        return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800';
      case 'employee':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
      case 'menu':
        return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
  };
  const exportLogs = () => {
    exportActivityLogToExcel({
      logs: filteredLogs.map(log => ({
        timestamp: log.timestamp,
        user: log.user,
        action: log.action,
        details: log.details,
        type: log.type
      }))
    });
  };
  return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-1211">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-1212">
        <div data-id="element-1213">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1214">
            Registro de Actividad
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-1215">
            Historial completo de acciones administrativas
          </p>
        </div>
        <Button leftIcon={<Download size={18} data-id="element-1217" />} onClick={exportLogs} disabled={filteredLogs.length === 0} data-id="element-1216">
          Exportar Registro
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4 mb-6" data-id="element-1218">
        <SearchBar placeholder="Buscar en registro..." className="w-full sm:flex-1" onSearch={setSearchTerm} data-id="element-1219" />
        <FilterDropdown label="Tipo" value={typeFilter} options={['Todos', 'Order', 'Company', 'Employee', 'Menu', 'System']} onChange={setTypeFilter} className="w-full sm:w-48" data-id="element-1220" />
      </div>

      <Card noPadding data-id="element-1221">
        {paginatedLogs.length === 0 ? <div className="text-center py-20 px-4" data-id="element-1222">
            <History size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-1223" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1224">
              No hay actividad registrada
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-1225">
              {searchTerm || typeFilter !== 'Todos' ? 'No se encontraron resultados con los filtros aplicados' : 'Las acciones administrativas aparecerán aquí'}
            </p>
          </div> : <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto" data-id="element-1226">
              <table className="w-full text-left border-collapse" data-id="element-1227">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-1228">
                  <tr data-id="element-1229">
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1230">
                      Fecha y Hora
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1231">
                      Usuario
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1232">
                      Acción
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1233">
                      Detalles
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1234">
                      Tipo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1235">
                  {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1236">
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 font-mono" data-id="element-1237">
                        {log.timestamp}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white" data-id="element-1238">
                        {log.user}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white" data-id="element-1239">
                        {log.action}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-1240">
                        {log.details}
                      </td>
                      <td className="px-4 py-3" data-id="element-1241">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(log.type)}`} data-id="element-1242">
                          {getIcon(log.type)}
                          {log.type}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden p-3 space-y-3" data-id="element-1243">
              {paginatedLogs.map(log => <div key={log.id} className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-1244">
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-1245">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border ${getTypeColor(log.type)}`} data-id="element-1246">
                      {getIcon(log.type)}
                      {log.type}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1" data-id="element-1247">
                      <Clock size={10} data-id="element-1248" />
                      {log.timestamp}
                    </span>
                  </div>

                  <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-1" data-id="element-1249">
                    {log.action}
                  </h3>

                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-2" data-id="element-1250">
                    {log.details}
                  </p>

                  <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-800" data-id="element-1251">
                    <Users size={12} data-id="element-1252" />
                    <span data-id="element-1253">{log.user}</span>
                  </div>
                </div>)}
            </div>
          </>}

        {/* Paginación */}
        {totalPages > 1 && <div className="px-4 pb-4" data-id="element-1254">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredLogs.length} itemsPerPage={itemsPerPage} data-id="element-1255" />
          </div>}
      </Card>
    </div>;
}